import { MutableRefObject, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  DEFINITIVE_REFUSAL_STATUSES,
  shouldKeepOperationKey,
} from '../utils/operationKeyOutcome';

// The outcome rule is pure (no React); re-exported so no import site breaks.
export { DEFINITIVE_REFUSAL_STATUSES, shouldKeepOperationKey };

export interface OperationKeyStore {
  /** The key of the current submit series; created on first use. */
  current(): string;
  /**
   * R2-4: the request body of the current series, built once. A retry resends
   * exactly this body with the same key, even if the form changed meanwhile.
   * R3-4: while the series is unresolved, `build` is not called at all.
   */
  request<T>(build: () => T): T;
  /** The pinned body of the current series, if any; never builds one. */
  peek<T>(): T | undefined;
  /**
   * Call after every attempt with its error (none on success) and the key the
   * attempt sent. A settle naming another key than the current series' is a
   * late answer of an earlier series and is ignored (nothing changes, nobody
   * is told). Without a key it settles whatever series is current.
   */
  settle(error?: unknown, key?: string): void;
  /**
   * A key is held: from the first `current()` of a series (so also while its
   * first attempt is in flight) until a success or a definitive refusal.
   * Read between attempts, true means the last attempt ended ambiguously and
   * may have committed unseen. For rendering use the handle's `pending`.
   */
  hasPending(): boolean;
  /** R3-4: called after every settle; returns the unsubscribe function. */
  subscribe(listener: () => void): () => void;
}

export interface OperationKeyHandle extends OperationKeyStore {
  /**
   * R3-4: the last attempt ended ambiguously and the series is unresolved. The
   * form shows UnconfirmedSaveBanner, locks its fields and resends the frozen
   * payload through `request` on the next submit.
   */
  readonly pending: boolean;
}

export function createOperationKeyStore(): OperationKeyStore {
  let pending: string | null = null;
  let pinned: { body: unknown } | null = null;
  const listeners = new Set<() => void>();
  return {
    current(): string {
      if (!pending) {
        pending = uuidv4();
      }
      return pending;
    },
    request<T>(build: () => T): T {
      if (!pinned) {
        pinned = { body: build() };
      }
      return pinned.body as T;
    },
    peek<T>(): T | undefined {
      return pinned ? (pinned.body as T) : undefined;
    },
    settle(error?: unknown, key?: string): void {
      if (key !== undefined && key !== pending) {
        return;
      }
      if (!shouldKeepOperationKey(error)) {
        pending = null;
        pinned = null;
      }
      listeners.forEach((listener) => listener());
    },
    hasPending(): boolean {
      return pending !== null;
    },
    subscribe(listener: () => void): () => void {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

export interface OperationKeyRegistry {
  /** The store of `scope`; its series lives here until it ends. */
  store(scope: string): OperationKeyStore;
  /** Scopes holding a series (in flight or unresolved). */
  size(): number;
  /** Forgets every series and tells every form showing one. */
  clear(): void;
}

/**
 * Scoped series, one entry per scope. An entry appears with the first
 * `current()`/`request()` and is deleted by the settle that ends the series,
 * so it outlives an unmounted form only while unresolved. Listeners are kept
 * per scope, apart from the entry, so every form showing the scope hears it.
 */
function createOperationKeyRegistry(): OperationKeyRegistry {
  const series = new Map<string, OperationKeyStore>();
  const listeners = new Map<string, Set<() => void>>();
  const live = (scope: string): OperationKeyStore => {
    let store = series.get(scope);
    if (!store) {
      store = createOperationKeyStore();
      series.set(scope, store);
    }
    return store;
  };
  return {
    store(scope: string): OperationKeyStore {
      return {
        current: (): string => live(scope).current(),
        request: <T>(build: () => T): T => live(scope).request(build),
        peek: <T>(): T | undefined => series.get(scope)?.peek<T>(),
        settle(error?: unknown, key?: string): void {
          const store = series.get(scope);
          if (
            key !== undefined &&
            !(store?.hasPending() && store.current() === key)
          ) {
            return;
          }
          store?.settle(error, key);
          if (!shouldKeepOperationKey(error)) {
            series.delete(scope);
          }
          listeners.get(scope)?.forEach((listener) => listener());
        },
        hasPending: (): boolean => series.get(scope)?.hasPending() ?? false,
        subscribe(listener: () => void): () => void {
          const scoped = listeners.get(scope) ?? new Set<() => void>();
          listeners.set(scope, scoped);
          scoped.add(listener);
          return () => {
            scoped.delete(listener);
            if (scoped.size === 0 && listeners.get(scope) === scoped) {
              listeners.delete(scope);
            }
          };
        },
      };
    },
    size: (): number => series.size,
    clear(): void {
      series.clear();
      listeners.forEach((scoped) => scoped.forEach((listener) => listener()));
    },
  };
}

let registry = createOperationKeyRegistry();
/** The Firebase account whose series the registry holds (R1-2). */
let registryAccount: string | null = null;

function ownStore(
  ref: MutableRefObject<OperationKeyStore | null>,
): OperationKeyStore {
  if (!ref.current) {
    ref.current = createOperationKeyStore();
  }
  return ref.current;
}

/**
 * The scope's series outside a render: the store `useOperationKey(scope)`
 * uses, so every form mounted on the scope hears its settles. For a submit
 * whose target the same submit creates (a new receipt's approval), which no
 * rendered handle can name yet.
 */
export function scopedOperationKeys(scope: string): OperationKeyStore {
  return registry.store(scope);
}

/**
 * Logout: forget every scoped series, so an unresolved one is never offered
 * to the next user of the same organization. A late answer of an attempt from
 * before the logout names a key no series holds, so it changes nothing.
 */
export function clearOperationKeyRegistry(): void {
  registry.clear();
}

/**
 * R1-2: call with every account Firebase's `onAuthStateChanged` reports (null
 * once signed out), which covers sign-ins and sign-outs made in other tabs. A
 * series belongs to the account that started it, so another account, whether
 * it replaces this one directly or signs in after a sign-out, first forgets
 * every series: an unresolved one is never offered to it, and a late answer
 * of the earlier account's attempt names a key no series holds. The same
 * account reported again (a re-fired observer, a sign-in back) keeps them.
 */
export function bindOperationKeysToAccount(uid: string | null): void {
  if (uid === null) {
    return;
  }
  if (registryAccount !== null && registryAccount !== uid) {
    registry.clear();
  }
  registryAccount = uid;
}

/** Test-only: forget every scoped series; returns the fresh registry. */
export function resetOperationKeyRegistryForTests(): OperationKeyRegistry {
  registry = createOperationKeyRegistry();
  registryAccount = null;
  return registry;
}

/**
 * A series scope: the operation and its target, e.g. `kor:<org>:<product>`,
 * with canonical lowercase ids. Without both ids there is no scope.
 */
export function operationKeyScope(
  operation: string,
  organizationId: string | undefined,
  targetId: string | undefined,
): string | undefined {
  return organizationId && targetId
    ? `${operation}:${organizationId}:${targetId}`.toLowerCase()
    : undefined;
}

/**
 * Without `scope`: one store per form (reviews D6, R2-4); the key and its body
 * survive re-renders and retries. With `scope`: the scope's series from the
 * registry, which also survives the form's unmount while unresolved (R3-4).
 * `pending` re-renders the form after each settle, so it can show
 * UnconfirmedSaveBanner and lock its fields; it follows a changed scope at once.
 */
export function useOperationKey(scope?: string): OperationKeyHandle {
  const own = useRef<OperationKeyStore | null>(null);
  const scoped = useMemo(
    () => (scope === undefined ? null : registry.store(scope)),
    [scope],
  );
  const keys = scoped ?? ownStore(own);
  const [pending, setPending] = useState<boolean>(() => keys.hasPending());
  const [shownKeys, setShownKeys] = useState<OperationKeyStore>(keys);
  if (shownKeys !== keys) {
    setShownKeys(keys);
    setPending(keys.hasPending());
  }
  useEffect(() => {
    const sync = (): void => setPending(keys.hasPending());
    // A settle between the render and this subscription would go unheard.
    sync();
    return keys.subscribe(sync);
  }, [keys]);
  const shownPending = shownKeys === keys ? pending : keys.hasPending();
  return useMemo(
    () => ({ ...keys, pending: shownPending }),
    [keys, shownPending],
  );
}
